"use client"

import { Popover, PopoverContent, PopoverTrigger, Tooltip } from "@coval/ui"
import { ChevronDown, Plus } from "lucide-react"

import type { CoviThreadMeta } from "../model/types"
import { CoviThreadList } from "./CoviThreadList"

/**
 * The one-line "which thread am I in" bar, directly under the panel header.
 *
 * The thread name is itself the switcher: clicking it opens a popover (Linear's
 * pattern) with a "New chat" action — whose destination belongs to the surface
 * that owns the bar: the docked panel starts one in place, Home returns to its
 * landing — and the full thread list. This replaces the
 * pinned thread-list sidebar — one thread is ever shown inline, and everything
 * else lives one click away in the popover, so the bar never changes size no
 * matter how many threads the user has.
 *
 * A background thread that is still streaming shows a live dot here, so leaving
 * a long answer running and switching away stays visible.
 *
 * On Sofia Home the bar is what the collapsed chat rail leaves in the header,
 * so history stays one click away while the rail is put away.
 */
export function CoviThreadBar({
  thread,
  threads,
  activeThreadId,
  streamingThreadIds,
  threadCount,
  backgroundStreaming,
  open,
  onOpenChange,
  onSelect,
  onNew,
  onRename,
  onTogglePin,
  onDelete,
}: {
  thread: CoviThreadMeta | null
  threads: CoviThreadMeta[]
  activeThreadId: string | null
  streamingThreadIds: ReadonlySet<string>
  threadCount: number
  /** True when some OTHER thread has a turn in flight. */
  backgroundStreaming: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (threadId: string) => void
  onNew: () => void
  onRename: (threadId: string, title: string) => void
  onTogglePin: (threadId: string) => void
  onDelete: (threadId: string) => void
}) {
  const title = thread?.title ?? "New chat"

  return (
    <div className="flex w-full min-w-0 items-center gap-0.5">
      <Popover open={open} onOpenChange={onOpenChange}>
        <Tooltip content="Switch chat">
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Switch chat"
              className="flex max-w-full min-w-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-coval-text transition-colors hover:bg-surface-2"
            >
              {backgroundStreaming ? (
                <span
                  aria-label="Another chat is still working"
                  className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-coval-info-text"
                />
              ) : null}
              <span className="min-w-0 truncate">{title}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-coval-text-muted" />
            </button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent align="start" className="w-72 p-0">
          <div role="dialog" aria-label="Sofia chats">
            <div className="p-1">
              <button
                type="button"
                onClick={() => {
                  onNew()
                  onOpenChange(false)
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-coval-text transition-colors hover:bg-surface-1"
              >
                <Plus className="h-4 w-4" />
                New chat
              </button>
            </div>
            {threadCount > 0 ? (
              <div className="border-t border-coval-border py-1">
                <CoviThreadList
                  threads={threads}
                  activeThreadId={activeThreadId}
                  streamingThreadIds={streamingThreadIds}
                  onSelect={(id) => {
                    onSelect(id)
                    onOpenChange(false)
                  }}
                  onRename={onRename}
                  onTogglePin={onTogglePin}
                  onDelete={onDelete}
                  variant="popover"
                />
              </div>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
